/**
 * The board's one reducer. Every change the host makes on the board goes through
 * `boardReducer`, so what gets saved is always what is on screen, and the
 * invariants the server checks (`validateBoardState`) are kept here rather than
 * discovered on a refused save.
 *
 * The reducer is pure: nothing here reads a clock, a random number or the
 * network. Cards that need a fresh id are built by the factories below and
 * handed to the `add` action already made.
 */
import type {
  BoardArrive,
  BoardBrand,
  BoardCard,
  BoardCardKind,
  BoardLane,
  BoardState,
} from "../../shared/types";
import { fitWalk } from "../../shared/board-walk";

/** The lanes, in tab order. */
const BOARD_LANES: readonly BoardLane[] = ["page", "room", "mail"];

/** The cards that hold a lane together: they cannot be switched off or deleted. */
function isFixedCard(card: BoardCard): boolean {
  return card.kind === "arrive";
}

/** The keys a card carries that identify it or find its rows: never patched. */
const PROTECTED = new Set(["id", "kind", "interactionId", "automationId"]);

/** An id nothing on the board is using yet, derived from the one asked for. */
export function freeId(state: BoardState, id: string): string {
  const taken = new Set(allCards(state).map((c) => c.id));
  if (!taken.has(id)) return id;
  let n = 2;
  while (taken.has(`${id}_${n}`)) n += 1;
  return `${id}_${n}`;
}

/** Every card on the board, in lane order. */
export function allCards(state: BoardState): BoardCard[] {
  return [...state.page, ...state.room, ...state.mail];
}

/** The card with this id, wherever it is. */
export function findCard(state: BoardState, id: string): BoardCard | undefined {
  return allCards(state).find((c) => c.id === id);
}

/** Which lane a card lives in, or null when it is not on the board. */
export function laneOf(state: BoardState, id: string): BoardLane | null {
  for (const lane of BOARD_LANES) if (state[lane].some((c) => c.id === id)) return lane;
  return null;
}

/** The address a page can live at: lower case, letters, numbers and dashes. */
export function slugifyAddress(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

/** The first index a lane will accept a card at: after its fixed first card. */
/** The first index a card may take: the room keeps its arrival card first. */
function floorFor(lane: BoardLane): number {
  return lane === "room" ? 1 : 0;
}

// ── the actions ──────────────────────────────────────────────────────────
/** A field edit: the card's own keys, never the ones that identify it. */
type CardPatch = Record<string, unknown>;

export type BoardAction =
  /** Switch a card on or off (the look and arrival cards have no switch). */
  | { type: "toggle"; id: string }
  /** Move a card into the deleted tray, remembering where it was. */
  | { type: "delete"; id: string }
  /** Put a deleted card back where it came from. */
  | { type: "restore"; id: string }
  /** Empty one lane's deleted tray. */
  | { type: "clear-trash"; lane: "page" | "room" | "mail" }
  /** Fold edited fields into one card. */
  | { type: "edit"; id: string; patch: CardPatch }
  /** Reorder the page. */
  | { type: "move"; id: string; to: number }
  /** Add a card that was built outside the reducer (a from-you block, a restore). */
  | { type: "add"; lane: BoardLane; card: BoardCard; at?: number }
  /** Put a rewritten card back in place of the one with the same id. */
  | { type: "replace"; card: BoardCard }
  /** `null` hands the page back to the workspace brand. */
  | { type: "brand"; brand: BoardBrand | null }
  /** The default arrival every moment inherits. */
  | { type: "arrive"; arrive: Partial<BoardArrive> }
  /** One moment's own arrival; `null` puts it back on the default. */
  | { type: "cardArrive"; id: string; arrive: Partial<BoardArrive> | null }
  | { type: "slug"; slug: string }
  | { type: "workingTitle"; title: string }
  /** A step of Walk it through said to look right, or not any more. */
  | { type: "looked"; step: string; on: boolean }
  /** The step of Walk it through the host is on. */
  | { type: "walkStep"; step: string }
  /** One moment's (or the offer's) conversation, as it now stands. */
  /** Take the server's board wholesale (a card operation, a read, a reload). */
  | { type: "adopt"; state: BoardState };

// ── the reducer ──────────────────────────────────────────────────────────
function mapLane(
  state: BoardState,
  lane: BoardLane,
  fn: (cards: BoardCard[]) => BoardCard[],
): BoardState {
  return { ...state, [lane]: fn([...state[lane]]) };
}

function patchCard(card: BoardCard, patch: CardPatch): BoardCard {
  const next = { ...card } as unknown as Record<string, unknown>;
  for (const [key, value] of Object.entries(patch)) {
    if (PROTECTED.has(key)) continue;
    next[key] = value;
  }
  return next as unknown as BoardCard;
}

/** Full screen always pauses the talk: the one arrival rule the host cannot break. */
function settleArrive(arrive: BoardArrive): BoardArrive {
  return arrive.where === "full" ? { ...arrive, pause: true } : arrive;
}

function editCard(state: BoardState, id: string, fn: (card: BoardCard) => BoardCard): BoardState {
  const lane = laneOf(state, id);
  if (!lane) return state;
  return mapLane(state, lane, (cards) => cards.map((c) => (c.id === id ? fn(c) : c)));
}

export function boardReducer(state: BoardState, action: BoardAction): BoardState {
  switch (action.type) {
    case "adopt":
      return keepOrder(action.state);

    case "toggle": {
      const card = findCard(state, action.id);
      if (!card || isFixedCard(card)) return state;
      return editCard(state, action.id, (c) => ({ ...c, on: !c.on }));
    }

    case "delete": {
      const lane = laneOf(state, action.id);
      const card = lane ? state[lane].find((c) => c.id === action.id) : undefined;
      if (!lane || !card || isFixedCard(card)) return state;
      const index = state[lane].findIndex((c) => c.id === action.id);
      return {
        ...state,
        [lane]: state[lane].filter((c) => c.id !== action.id),
        trash: [...state.trash, { lane, index, card, at: Date.now() }],
      };
    }

    case "clear-trash":
      return { ...state, trash: state.trash.filter((t) => t.lane !== action.lane) };
    case "restore": {
      const entry = state.trash.find((t) => t.card.id === action.id);
      if (!entry) return state;
      const rest = state.trash.filter((t) => t.card.id !== action.id);
      const lane = entry.lane;
      const cards = [...state[lane]];
      const card = { ...entry.card, id: freeId(state, entry.card.id) };
      // A deleted follow-up is only taken off this webinar, never destroyed:
      // the card keeps its automation, and the next save puts it back on.
      const floor = floorFor(lane);
      cards.splice(Math.min(Math.max(entry.index, floor), cards.length), 0, card);
      return { ...state, [lane]: cards, trash: rest };
    }

    case "edit":
      return editCard(state, action.id, (c) => patchCard(c, action.patch));

    case "move": {
      const lane = laneOf(state, action.id);
      if (!lane) return state;
      const floor = floorFor(lane);
      const from = state[lane].findIndex((c) => c.id === action.id);
      if (from < floor) return state;
      const to = Math.min(Math.max(action.to, floor), state[lane].length - 1);
      if (to === from) return state;
      return mapLane(state, lane, (cards) => {
        const [card] = cards.splice(from, 1);
        cards.splice(to, 0, card);
        return cards;
      });
    }

    case "add": {
      const lane = action.lane;
      const card = { ...action.card, id: freeId(state, action.card.id) };
      const floor = floorFor(lane);
      const at = action.at === undefined ? state[lane].length : action.at;
      return mapLane(state, lane, (cards) => {
        cards.splice(Math.min(Math.max(at, floor), cards.length), 0, card);
        return cards;
      });
    }

    case "replace": {
      const lane = laneOf(state, action.card.id);
      if (!lane) return state;
      return mapLane(state, lane, (cards) =>
        cards.map((c) => (c.id === action.card.id ? action.card : c)),
      );
    }

    case "brand":
      return { ...state, brand: action.brand };

    case "arrive":
      return { ...state, arrive: settleArrive({ ...state.arrive, ...action.arrive }) };

    case "cardArrive":
      return editCard(state, action.id, (c) => {
        const next = { ...c } as BoardCard & { arrive?: BoardArrive };
        if (action.arrive === null) delete next.arrive;
        else next.arrive = settleArrive({ ...arriveOf(state, c), ...action.arrive });
        return next;
      });

    case "slug":
      return { ...state, slug: slugifyAddress(action.slug) };

    case "workingTitle":
      return { ...state, workingTitle: action.title.slice(0, 60) };

    case "looked": {
      const was = state.walk?.looked ?? [];
      const looked = action.on ? [...new Set([...was, action.step])] : was.filter((s) => s !== action.step);
      return { ...state, walk: fitWalk({ ...state.walk, looked }) };
    }

    case "walkStep":
      return state.walk?.step === action.step ? state : { ...state, walk: fitWalk({ ...state.walk, step: action.step }) };

    default:
      return state;
  }
}

/** How one moment arrives: its own settings when it has them, else the default. */
function arriveOf(state: BoardState, card: BoardCard): BoardArrive {
  const own = (card as { arrive?: BoardArrive }).arrive;
  return own ?? state.arrive;
}

/**
 * The kinds a board may still carry from before, which nothing builds any more.
 * The player's chapter list is the page's own Chapters card now, so the room
 * card that used to switch it went; a board saved with one still opens, and the
 * card is dropped the moment it is read.
 */
const GONE: ReadonlySet<string> = new Set(["playerChapters"]);

/** Everything a lane still has a use for. */
function kept(cards: BoardCard[]): BoardCard[] {
  return cards.some((c) => GONE.has(c.kind)) ? cards.filter((c) => !GONE.has(c.kind)) : cards;
}

/**
 * The one rule a lane cannot lose: the arrival card opens the room. A board
 * that comes back from anywhere is run through this, so a stored board written
 * before a card moved still opens.
 */
export function keepOrder(state: BoardState): BoardState {
  return {
    ...state,
    page: kept(state.page),
    room: first(kept(state.room), "arrive"),
    mail: kept(state.mail),
  };
}

function first(cards: BoardCard[], kind: BoardCardKind): BoardCard[] {
  const index = cards.findIndex((c) => c.kind === kind);
  if (index <= 0) return cards;
  const copy = [...cards];
  const [card] = copy.splice(index, 1);
  copy.unshift(card);
  return copy;
}

// ── the cards the board builds on its own ────────────────────────────────

